import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { ABSENCE_TYPE_LABELS, canViewPage, certTypeMatchesLoose, type AbsenceType } from '@cls/shared';
import { AbsenceModal } from './AbsenceModal.js';
import { useAuth } from '../../features/auth/AuthProvider.js';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { waLink } from '../../lib/wa.js';
import { AddWorkerModal } from './AddWorkerModal.js';
import { ProximityModal } from './ProximityModal.js';
import { WorkerNotesModal } from './WorkerNotesModal.js';
import { ShareTrainingModal } from './ShareTrainingModal.js';
import { PhotoModal } from './PhotoModal.js';
import { QrSignInModal } from './QrSignInModal.js';
import { DnaEditModal } from './DnaEditModal.js';
import { WaIcon } from '../primitives/WaIcon.js';
import { DeploymentDetailModal } from './DeploymentDetailModal.js';
import { CertNotifyModal } from './CertNotifyModal.js';

type SigninFilter = 'all' | 'in' | 'out' | 'today';
type DeployFilter = 'all' | 'active' | 'completed';
type CertFilter = 'all' | 'valid' | 'expiring' | 'expired';

type Tab = 'profile' | 'training' | 'notes' | 'deploy' | 'signin' | 'signeddocs' | 'dna';

interface DnaRecord {
  id: string;
  code: string;
  workerId: string;
  workerName?: string;
  projectId: string;
  projectName?: string | null;
  scheduledDate: string;
  scheduledTime: string | null;
  recordedAt?: string;
  recordedBy?: string | null;
  notes?: string | null;
  createdAt?: string;
}

interface Cert {
  id: string;
  type: string;
  expiry: string | null;
  status: 'valid' | 'expiring' | 'expired';
  notes?: string | null;
  docRef?: string | null;
  docUrl?: string | null;
}

interface Note {
  id: string;
  text: string;
  date: string;
  by?: string | null;
}

interface EmergencyContact {
  name?: string;
  phone?: string;
  relationship?: string;
}

interface Worker {
  id: string;
  name: string;
  role: string;
  roles?: string[];
  status: 'available' | 'deployed' | 'unavailable';
  postcode: string;
  phone: string;
  email: string | null;
  address: string | null;
  photo: string | null;
  core: boolean;
  rating: number | null;
  companyRef: string | null;
  payrollRef: string | null;
  agencyCode: string | null;
  rtwStatus: string | null;
  rtwExpiry: string | null;
  nationality: string | null;
  onSiteUntil: string | null;
  emergency: EmergencyContact | null;
  projects: string[];
  certs: Cert[];
  notes: Note[];
}

interface ProjectLite {
  id: string;
  code: string;
  name: string;
  status: string;
}

interface Deployment {
  id: string;
  workerId: string;
  projectId: string;
  projectName: string | null;
  startDate: string;
  endDate: string | null;
  duration: string | null;
  status: string;
}

interface SignInRecord {
  id: string;
  workerId: string;
  type: 'in' | 'out';
  projectId: string;
  projectName: string | null;
  timestamp: string;
}

interface AvailabilitySignal {
  id: string;
  workerId: string;
  signaledAt: string;
}

interface WorkerAbsence {
  id: string;
  workerId: string;
  type: AbsenceType;
  startDate: string;
  endDate: string;
  notes: string | null;
  createdBy: string | null;
}

interface RoleRequirement {
  role: string;
  mandatory: string[];
  beneficial: string[];
}

interface QrPayload {
  workerId: string;
  value: string;
  dataUrl: string;
}

interface Props {
  workerId: string;
  onClose: () => void;
}

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

const AVATAR_PALETTE = ['#E8A427', '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4', '#F97316', '#10B981', '#EC4899', '#14B8A6'];
function avatarColor(name: string): string {
  if (!name) return AVATAR_PALETTE[0];
  return AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];
}

function signalAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.floor(ms / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  return Math.floor(hrs / 24) + 'd ago';
}

function certStatusColor(s: string): string {
  return s === 'valid' ? '#22C55E' : s === 'expiring' ? '#F59E0B' : '#EF4444';
}

function certStatusBg(s: string): string {
  return s === 'valid' ? 'rgba(34,197,94,.15)' : s === 'expiring' ? 'rgba(245,158,11,.18)' : 'rgba(239,68,68,.18)';
}

function CertEditInline({ cert, onCancel, onSave, saving }: { cert: Cert; onCancel: () => void; onSave: (p: { type: string; expiry: string | null; status: 'valid' | 'expiring' | 'expired'; docRef: string | null }) => void; saving: boolean }) {
  const [type, setType] = useState(cert.type);
  const [expiry, setExpiry] = useState(cert.expiry?.slice(0, 10) ?? '');
  const [status, setStatus] = useState<'valid' | 'expiring' | 'expired'>(cert.status);
  const [docRef, setDocRef] = useState((cert as any).docRef ?? '');
  return (
    <div style={{ marginTop: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 9, fontFamily: 'Syne, sans-serif' }}>✏ Edit Certificate</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginBottom: 9 }}>
        <div>
          <label className="form-label">Certificate Type</label>
          <input type="text" className="form-input" value={type} onChange={(e) => setType(e.target.value)} />
        </div>
        <div>
          <label className="form-label">Expiry Date</label>
          <input type="date" className="form-input" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </div>
      </div>
      <div style={{ marginBottom: 9 }}>
        <label className="form-label">Status</label>
        <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value as 'valid' | 'expiring' | 'expired')}>
          <option value="valid">✓ Valid</option>
          <option value="expiring">⏱ Expiring Soon</option>
          <option value="expired">✗ Expired</option>
        </select>
      </div>
      <div style={{ marginBottom: 9 }}>
        <label className="form-label">Document Reference</label>
        <input type="text" className="form-input" value={docRef} onChange={(e) => setDocRef(e.target.value)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={() => onSave({ type: type.trim(), expiry: expiry || null, status, docRef: docRef.trim() || null })} disabled={saving || !type.trim()}>
          {saving ? 'Saving…' : '✓ Save'}
        </button>
      </div>
    </div>
  );
}

function emergencyDisplay(e: EmergencyContact | null | undefined): string {
  if (!e) return '';
  if (typeof e === 'string') return e;
  const parts: string[] = [];
  if (e.name) parts.push(e.name);
  if (e.phone) parts.push(e.phone);
  return parts.join(' ');
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  return s.slice(0, 10);
}

export function WorkerDetail({ workerId, onClose }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const canUseComms = isAdmin || user?.role === 'Manager';
  const canOpenProject = !!user && canViewPage(user, 'projects');
  const [tab, setTab] = useState<Tab>('profile');
  const [signinFilter, setSigninFilter] = useState<SigninFilter>('all');
  const [deployFilter, setDeployFilter] = useState<DeployFilter>('all');
  const [certFilter, setCertFilter] = useState<CertFilter>('all');
  const [addCertInline, setAddCertInline] = useState(false);
  const [editCertInlineId, setEditCertInlineId] = useState<string | null>(null);
  const [notifyExpiryOpen, setNotifyExpiryOpen] = useState(false);
  const [notifyMissingOpen, setNotifyMissingOpen] = useState(false);
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [newCertType, setNewCertType] = useState('');
  const [newCertExpiry, setNewCertExpiry] = useState('');
  const [newCertStatus, setNewCertStatus] = useState<'valid' | 'expiring' | 'expired'>('valid');
  const [newCertDocRef, setNewCertDocRef] = useState('');
  const [newCertFile, setNewCertFile] = useState<File | null>(null);
  const [newCertDropOver, setNewCertDropOver] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [proxOpen, setProxOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [dnaAddOpen, setDnaAddOpen] = useState(false);
  const [dnaEdit, setDnaEdit] = useState<DnaRecord | null>(null);
  const [openDeployId, setOpenDeployId] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: w } = useQuery({
    queryKey: ['workers', workerId],
    queryFn: () => api.get<Worker>(`/workers/${workerId}`).then((r) => r.data),
  });
  const { data: deployments = [] } = useQuery({
    queryKey: ['deployments', 'by-worker', workerId],
    queryFn: () => api.get<Deployment[]>(`/workers/${workerId}/deployments`).then((r) => r.data),
    enabled: !!workerId,
  });
  const { data: signinRecords = [] } = useQuery({
    queryKey: ['signin-records', 'by-worker', workerId],
    queryFn: () => api.get<SignInRecord[]>(`/signin-records?workerId=${workerId}`).then((r) => r.data),
    enabled: !!workerId,
  });
  const { data: sharedDocs = [] } = useQuery({
    queryKey: ['shared-docs', 'by-worker', workerId],
    queryFn: () => api.get<{ id: string; name: string; createdAt: string; createdByName: string | null; recipients: { status: string; signedAt: string | null; notifiedAt: string | null }[] }[]>(`/shared-docs?workerId=${workerId}`).then((r) => r.data),
    enabled: !!workerId,
  });
  const { data: absences = [] } = useQuery({
    queryKey: ['absences', workerId],
    queryFn: () => api.get<WorkerAbsence[]>(`/absences?workerId=${workerId}`).then((r) => r.data).catch(() => [] as WorkerAbsence[]),
    enabled: !!workerId,
  });
  const { data: availSignal } = useQuery({
    queryKey: ['availability-signals', 'recent-for', workerId],
    queryFn: () => api.get<AvailabilitySignal | null>(`/availability-signals/recent-for/${workerId}`).then((r) => r.data),
    enabled: !!workerId,
  });
  const { data: roleReqs = [] } = useQuery({
    queryKey: ['training', 'role-requirements'],
    queryFn: () => api.get<RoleRequirement[]>('/training/role-requirements').then((r) => r.data),
  });
  const { data: qr } = useQuery({
    queryKey: ['qr', 'worker', workerId],
    queryFn: () => api.get<QrPayload>(`/qr/worker/${workerId}`).then((r) => r.data),
    enabled: !!workerId,
  });
  const { data: allProjects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectLite[]>('/projects').then((r) => r.data),
  });
  const { data: dnaForWorker = [] } = useQuery({
    queryKey: ['dna-records', 'by-worker', workerId],
    queryFn: () => api.get<DnaRecord[]>(`/dna-records?workerId=${workerId}`).then((r) => r.data),
    enabled: !!workerId,
  });

  const signalAvailable = useMutation({
    mutationFn: () => api.post('/availability-signals', { workerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers'] });
      qc.invalidateQueries({ queryKey: ['availability-signals', 'recent-for', workerId] });
      toast.success('Availability signal sent');
    },
    onError: () => toast.warn('Already signalled', 'A recent availability signal exists'),
  });

  const [noteText, setNoteText] = useState('');
  const addNote = useMutation({
    mutationFn: (text: string) => api.post(`/workers/${workerId}/notes`, { text }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers', workerId] });
      setNoteText('');
      toast.success('Note added');
    },
    onError: () => toast.error('Add note failed'),
  });
  const deleteNote = useMutation({
    mutationFn: (noteId: string) => api.delete(`/workers/notes/${noteId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers', workerId] });
      toast.success('Note deleted');
    },
    onError: () => toast.error('Delete note failed'),
  });

  const deleteAbsence = useMutation({
    mutationFn: (absenceId: string) => api.delete(`/absences/${absenceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['absences'] });
      toast.info('Absence removed');
    },
    onError: () => toast.error('Delete failed'),
  });

  const deleteDna = useMutation({
    mutationFn: (dnaId: string) => api.delete(`/dna-records/${dnaId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dna-records'] });
      qc.invalidateQueries({ queryKey: ['dna-records', 'by-worker', workerId] });
      toast.success('DNA record deleted');
    },
    onError: () => toast.error('Delete failed'),
  });

  const uploadCertDoc = useMutation({
    mutationFn: async ({ certId, file }: { certId: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post<{ url: string; originalName?: string }>('/uploads/file', fd);
      await api.patch(`/workers/certs/${certId}`, { docRef: up.data.originalName ?? up.data.url, docUrl: up.data.url });
      return up.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers', workerId] });
      toast.success('Document attached');
    },
    onError: () => toast.error('Upload failed'),
  });
  const removeCertDoc = useMutation({
    mutationFn: (certId: string) => api.patch(`/workers/certs/${certId}`, { docRef: null, docUrl: null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers', workerId] });
      toast.success('Document removed');
    },
    onError: () => toast.error('Remove failed'),
  });
  const saveNewCert = useMutation({
    mutationFn: async () => {
      let docRef = newCertDocRef.trim() || null;
      let docUrl: string | null = null;
      if (newCertFile) {
        const fd = new FormData();
        fd.append('file', newCertFile);
        const up = await api.post<{ url: string; originalName?: string }>('/uploads/file', fd);
        docUrl = up.data.url ?? null;
        docRef = up.data.originalName ?? up.data.url ?? docRef;
      }
      return api.post(`/workers/${workerId}/certs`, {
        type: newCertType.trim(),
        expiry: newCertExpiry || null,
        status: newCertStatus,
        docRef,
        docUrl,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers'] });
      qc.invalidateQueries({ queryKey: ['workers', workerId] });
      qc.invalidateQueries({ queryKey: ['training', 'matrix'] });
      toast.success('Certificate added', newCertType);
      setAddCertInline(false);
      setNewCertType('');
      setNewCertExpiry('');
      setNewCertStatus('valid');
      setNewCertDocRef('');
      setNewCertFile(null);
    },
    onError: (e: any) => toast.error('Add cert failed', e?.response?.data?.message ?? 'Check fields'),
  });

  const updateCert = useMutation({
    mutationFn: async (input: { certId: string; type: string; expiry: string | null; status: 'valid' | 'expiring' | 'expired'; docRef: string | null }) => {
      return api.patch(`/workers/certs/${input.certId}`, {
        type: input.type,
        expiry: input.expiry,
        status: input.status,
        docRef: input.docRef,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers'] });
      qc.invalidateQueries({ queryKey: ['workers', workerId] });
      qc.invalidateQueries({ queryKey: ['training', 'matrix'] });
      toast.success('Certificate updated');
      setEditCertInlineId(null);
    },
    onError: (e: any) => toast.error('Update failed', e?.response?.data?.message ?? 'Check fields'),
  });

  const removeCert = useMutation({
    mutationFn: (certId: string) => api.delete(`/workers/certs/${certId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers', workerId] });
      toast.success('Certificate removed');
    },
    onError: () => toast.error('Delete failed'),
  });

  const changeStatus = async (s: 'available' | 'deployed' | 'unavailable' | 'dnu') => {
    if (!w) return;
    await api.patch(`/workers/${workerId}`, { status: s });
    qc.invalidateQueries({ queryKey: ['workers'] });
  };

  if (!w) return null;

  // Primary role is always first; additional roles fold in after, de-duped.
  // Empty / falsy values are dropped so we never end up with "" in the list.
  const allRoles = Array.from(new Set([w.role, ...(w.roles ?? [])].filter((r): r is string => !!r && r.length > 0)));
  const certOk = w.certs.filter((c) => c.status === 'valid').length;
  const certExp = w.certs.filter((c) => c.status === 'expired').length;
  const certWarn = w.certs.filter((c) => c.status === 'expiring').length;
  const mandatoryForRoles = Array.from(new Set(allRoles.flatMap((r) => roleReqs.find((rq) => rq.role === r)?.mandatory ?? [])));
  const beneficialForRoles = Array.from(new Set(allRoles.flatMap((r) => roleReqs.find((rq) => rq.role === r)?.beneficial ?? []).filter((b) => !mandatoryForRoles.includes(b))));
  const certTypeMatches = certTypeMatchesLoose;
  const missingMandatory = mandatoryForRoles.filter((ct) => !w.certs.find((cv) => certTypeMatches(cv.type, ct)));
  const signinCount = signinRecords.length;
  const workerDocs = sharedDocs.map((d) => ({ id: d.id, name: d.name, createdAt: d.createdAt, sharedBy: d.createdByName, status: d.recipients[0]?.status ?? 'pending', signedAt: d.recipients[0]?.signedAt ?? null, notifiedAt: d.recipients[0]?.notifiedAt ?? null }));
  const signedDocsCount = workerDocs.filter((d) => d.status === 'signed').length;
  const currentProjects = (w.projects || []).map((code) => allProjects.find((p) => p.code === code)).filter(Boolean) as ProjectLite[];
  const recentDeployments = deployments.slice(0, 5);
  const lastSignin = signinRecords.length ? signinRecords[0] : null;

  const printQr = () => {
    if (!qr) return;
    const html = `<html><head><title>QR — ${w.name}</title><style>body{font-family:Arial;text-align:center;padding:40px}img{width:300px;height:300px}h1{font-size:18px;margin:10px 0}p{color:#666;font-size:12px;margin:6px 0}</style></head><body><img src="${qr.dataUrl}"/><h1>${w.name}</h1><p><strong>${qr.value}</strong></p><p>Scan to identify · Sign in/out · Verify identity</p></body></html>`;
    const win = window.open('', '_blank', 'width=420,height=520');
    if (!win) {
      toast.warn('Popup blocked', 'Allow popups to print the QR');
      return;
    }
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 250);
    toast.info('QR Sent to Printer', `${w.name} · ${qr.value}`);
  };
  const saveQr = () => {
    if (!qr) return;
    const a = document.createElement('a');
    a.href = qr.dataUrl;
    a.download = `${w.name.replace(/\s+/g, '_')}_QR.png`;
    a.click();
    toast.success('QR Downloaded', `${w.name.replace(/\s+/g, '_')}_QR.png`);
  };

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" id="wdetail-body" style={{ width: 1170, maxWidth: '98vw', minHeight: 600, display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ position: 'relative', width: 64, height: 64, flexShrink: 0 }}>
              {w.photo ? (
                <img src={w.photo} alt={w.name} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--accent)' }} />
              ) : (
                <div className="av" style={{ width: 64, height: 64, fontSize: 19, background: avatarColor(w.name), border: '2px solid var(--accent)' }}>
                  {initials(w.name)}
                </div>
              )}
              {isAdmin ? <button
                onClick={() => setPhotoOpen(true)}
                title="Update photo"
                style={{
                  position: 'absolute',
                  right: -2,
                  bottom: -2,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: 'var(--accent)',
                  color: '#0B0F1A',
                  border: '2px solid var(--surface)',
                  cursor: 'pointer',
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  lineHeight: 1,
                }}
              >📷</button> : null}
            </div>
            <div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span>{w.name}</span>
                {w.core ? <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(232,164,39,.18)', color: 'var(--accent)', fontWeight: 700 }}>CORE</span> : null}
                {w.rating != null && w.rating > 0 ? (
                  <span style={{ color: '#F59E0B', fontSize: 13, letterSpacing: 1 }}>{'★'.repeat(Math.round(w.rating))}</span>
                ) : null}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{w.role}</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {!isAdmin ? <span className={`badge b-${w.status}`}>{w.status}</span> : <select
                  value={w.status}
                  onChange={(e) => changeStatus(e.target.value as 'available' | 'deployed' | 'unavailable' | 'dnu')}
                  style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 11, padding: '3px 8px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                >
                  <option value="available">● Available</option>
                  <option value="deployed">● Deployed</option>
                  <option value="unavailable">● Unavailable</option>
                  <option value="dnu">● DNU (Do Not Use)</option>
                </select>}
                {w.onSiteUntil ? <span style={{ fontSize: 10, color: 'var(--muted)' }}>on site until {w.onSiteUntil.slice(0, 10)}</span> : null}
                {w.companyRef ? <span style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 5, padding: '2px 7px', fontSize: 10, color: 'var(--accent)' }}>Co: {w.companyRef}</span> : null}
                {w.payrollRef ? <span style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 5, padding: '2px 7px', fontSize: 10, color: 'var(--info)' }}>Pay: {w.payrollRef}</span> : null}
                {w.agencyCode ? <span style={{ background: 'rgba(124,58,237,.2)', color: '#a855f7', borderRadius: 5, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>{w.agencyCode}</span> : null}
                {w.nationality ? <span style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 5, padding: '2px 7px', fontSize: 10, color: 'var(--muted)' }} title="Nationality">🌐 {w.nationality}</span> : null}
                {dnaForWorker.length > 0 ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setTab('dna')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('dna'); } }}
                    title={`${dnaForWorker.length} DNA record${dnaForWorker.length !== 1 ? 's' : ''} — click for full history`}
                    style={{ background: 'rgba(239,68,68,.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,.4)', borderRadius: 5, padding: '2px 7px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}
                  >
                    ⚠ {dnaForWorker.length} DNA
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {availSignal ? (
              <div title={`Signaled available ${signalAge(availSignal.signaledAt)}`} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(34,197,94,.12)', border: '1px solid rgba(34,197,94,.35)', borderRadius: 8, padding: '5px 11px', fontSize: 11, color: '#22c55e', fontWeight: 600 }}>
                🟢 Available signal · {signalAge(availSignal.signaledAt)}
              </div>
            ) : !isAdmin ? null : (
              <button
                className="btn btn-sm"
                style={{ background: '#16a34a', color: '#fff', border: '1px solid #15803d', fontWeight: 700 }}
                onClick={() => signalAvailable.mutate()}
                disabled={signalAvailable.isPending}
                title="Worker has told us they're available — flip status + log it"
              >
                🟢 Notify us — I'm available!
              </button>
            )}
            {isAdmin ? <button className="btn btn-primary btn-sm" onClick={() => setEditOpen(true)}>✏ Edit Profile</button> : null}
            <button className="close-btn" onClick={onClose} style={{ fontSize: 22 }}>×</button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
          {qr ? (
            <img src={qr.dataUrl} alt="Worker QR" style={{ width: 64, height: 64, flexShrink: 0, borderRadius: 6 }} />
          ) : (
            <div style={{ width: 64, height: 64, flexShrink: 0, background: 'var(--surface)', borderRadius: 6 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'Syne, sans-serif', marginBottom: 3 }}>Worker QR Code</div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 13, color: 'var(--accent)' }}>
              {w.companyRef || <span style={{ color: 'var(--muted)', fontWeight: 600, fontStyle: 'italic' }}>No code assigned</span>}
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 1 }}>Scan to identify · Sign in/out · Verify identity</div>
            <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={printQr} disabled={!qr}>🖨 Print</button>
              <button className="btn btn-ghost btn-sm" onClick={saveQr} disabled={!qr}>⬇ Save</button>
            </div>
          </div>
        </div>

        <div className="tab-bar">
          {([
            { k: 'profile' as Tab, lbl: <>👤 Profile</> },
            {
              k: 'training' as Tab,
              lbl: (
                <>
                  ◫ Training ({w.certs.length})
                  {certExp > 0 ? <span style={{ color: 'var(--danger)', marginLeft: 4 }}>⚠{certExp}</span> : certWarn > 0 ? <span style={{ color: 'var(--warn)', marginLeft: 4 }}>⏱{certWarn}</span> : null}
                  {missingMandatory.length > 0 ? <span style={{ color: 'var(--danger)', fontSize: 9, marginLeft: 4 }}> ✗{missingMandatory.length} missing</span> : null}
                </>
              ),
            },
            { k: 'notes' as Tab, lbl: <>📝 Notes ({w.notes.length})</> },
            { k: 'deploy' as Tab, lbl: <>📋 Deploy ({deployments.length})</> },
            { k: 'signin' as Tab, lbl: <>✅ Sign In/Out ({signinCount})</> },
            { k: 'signeddocs' as Tab, lbl: <>✍ Signed Docs ({signedDocsCount})</> },
            ...(dnaForWorker.length > 0 ? [{ k: 'dna' as Tab, lbl: <span style={{ color: 'var(--danger)' }}>⚠ DNA ({dnaForWorker.length})</span> }] : []),
          ]).map((t) => (
            <div
              key={t.k}
              className={`tab ${tab === t.k ? 'active' : ''}`}
              id={`wtab-${t.k}`}
              onClick={() => setTab(t.k)}
            >
              {t.lbl}
            </div>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {tab === 'profile' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18 }}>
              <div>
                <div className="sec-title">Contact &amp; Identity</div>
                <div className="dp-row"><span className="dp-key">Mobile</span><span className="dp-val">{w.phone ? <a href={`tel:${w.phone}`} style={{ color: 'var(--text)', textDecoration: 'none', fontSize: 12 }}>{w.phone}</a> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
                <div className="dp-row"><span className="dp-key">Email</span><span className="dp-val">{w.email ? <a href={`mailto:${w.email}`} style={{ color: 'var(--info)', fontSize: 11 }}>{w.email}</a> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
                <div className="dp-row"><span className="dp-key">Address</span><span className="dp-val" style={{ fontSize: 11, color: w.address ? 'var(--muted)' : 'var(--faint)' }}>{w.address || '—'}</span></div>
                <div className="dp-row"><span className="dp-key">Postcode</span><span className="dp-val">{w.postcode ? <a href={`https://www.google.com/maps/search/${encodeURIComponent(w.postcode)}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, textDecoration: 'none', fontSize: 11 }}>📍{w.postcode} ↗</a> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
                <div className="dp-row"><span className="dp-key">Emergency</span><span className="dp-val" style={{ fontSize: 12, fontWeight: 600, color: emergencyDisplay(w.emergency) ? 'var(--warn)' : 'var(--faint)' }}>{emergencyDisplay(w.emergency) || '—'}</span></div>
                <div className="dp-row"><span className="dp-key">Co. Ref</span><span className="dp-val" style={{ fontSize: 11, color: w.companyRef ? 'var(--accent)' : 'var(--faint)' }}>{w.companyRef || '—'}</span></div>
                <div className="dp-row"><span className="dp-key">Payroll</span><span className="dp-val" style={{ fontSize: 11, color: w.payrollRef ? 'var(--info)' : 'var(--faint)' }}>{w.payrollRef || '—'}</span></div>
                <div className="dp-row"><span className="dp-key">Agency</span><span className="dp-val">{w.agencyCode ? <span style={{ background: 'rgba(124,58,237,.2)', color: '#a855f7', borderRadius: 5, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>{w.agencyCode}</span> : <span style={{ color: 'var(--faint)', fontSize: 11 }}>—</span>}</span></div>
                <div className="dp-row"><span className="dp-key">Nationality</span><span className="dp-val" style={{ fontSize: 11, color: w.nationality ? 'var(--text)' : 'var(--faint)' }}>{w.nationality || '—'}</span></div>
                <div className="dp-row"><span className="dp-key">Right to Work</span><span className="dp-val" style={{ fontSize: 11 }}>{w.rtwStatus ? <span style={{ color: 'var(--text)' }}>{w.rtwStatus.toUpperCase()}{w.rtwExpiry ? <span style={{ color: 'var(--muted)', marginLeft: 6 }}>· exp {fmtDate(w.rtwExpiry)}</span> : null}</span> : <span style={{ color: 'var(--faint)' }}>—</span>}</span></div>
                <div className="divider" style={{ margin: '12px 0', height: 1, background: 'var(--border)' }}></div>
                <div className="sec-title">Actions</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <a className="btn" style={{ background: 'rgba(37,211,102,.15)', color: '#22c55e', border: '1px solid rgba(37,211,102,.25)', justifyContent: 'center', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }} href={waLink(w.phone, `Hi ${w.name.split(' ')[0]}, this is CLS. `)} target="_blank" rel="noreferrer"><WaIcon size={13} /> WhatsApp</a>
                  {w.email ? <a className="btn btn-info" style={{ justifyContent: 'center', textDecoration: 'none' }} href={`mailto:${w.email}`}>✉ Email</a> : null}
                  {canUseComms ? <button className="btn btn-ghost" style={{ justifyContent: 'center' }} onClick={() => setProxOpen(true)}>📍 Proximity Map</button> : null}
                  <button className="btn btn-ghost" style={{ justifyContent: 'center' }} onClick={() => setTab('deploy')}>📋 All Deploy Records</button>
                  <div style={{ marginTop: 4, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5 }}>Site Sign In / Out</div>
                    {isAdmin ? <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 12, padding: '8px 12px' }} onClick={() => setQrOpen(true)}>✅ Sign In</button> : null}
                    {lastSignin ? <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 4, textAlign: 'center' }}>Last: {lastSignin.type === 'in' ? 'Signed in to ' : 'Signed out of '}{lastSignin.projectName ?? lastSignin.projectId} at {new Date(lastSignin.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div> : null}
                  </div>
                </div>
              </div>

              <div>
                {currentProjects.length ? (
                  <>
                    <div className="sec-title">Current Projects</div>
                    {currentProjects.map((p) => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: 7, borderRadius: 8, border: '1px solid var(--border)', marginBottom: 6, background: 'var(--card)' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.status === 'live' ? '#22C55E' : 'var(--muted)' }}></span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 500 }}>{p.name}</div>
                          <div style={{ fontSize: 9, color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{p.code}</div>
                        </div>
                      </div>
                    ))}
                    <div className="divider" style={{ margin: '12px 0', height: 1, background: 'var(--border)' }}></div>
                  </>
                ) : null}
                {dnaForWorker.length > 0 ? (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setTab('dna')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('dna'); } }}
                    title="Click to view full DNA history"
                    style={{
                      background: 'rgba(239,68,68,.1)',
                      border: '1px solid rgba(239,68,68,.4)',
                      borderRadius: 10,
                      padding: 12,
                      marginBottom: 12,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <div style={{ fontSize: 22 }}>⚠</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#ef4444' }}>{dnaForWorker.length} DNA Record{dnaForWorker.length !== 1 ? 's' : ''}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>Click to view full DNA history</div>
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: 18 }}>→</div>
                  </div>
                ) : null}
                {recentDeployments.length ? (
                  <>
                    <div className="sec-title">Recent Deployments</div>
                    {recentDeployments.map((r) => {
                      const label = r.projectName ?? '—';
                      return (
                        <div
                          key={r.id}
                          onClick={() => setOpenDeployId(r.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: '1px solid rgba(30,42,64,.4)', cursor: 'pointer' }}
                          title="Open deployment record"
                        >
                          <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, color: 'var(--faint)', fontSize: 9, minWidth: 55 }}>{r.id.slice(0, 8)}</span>
                          <div style={{ flex: 1, fontSize: 11 }}>
                            <span style={{ fontWeight: 500, color: 'var(--accent)' }}>{label}</span>{' '}
                            <span style={{ color: 'var(--muted)' }}>{fmtDate(r.startDate)}</span>
                          </div>
                          <span className={`badge b-${r.status}`} style={{ fontSize: 8 }}>{r.status}</span>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>No deployment history</div>
                )}
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div className="sec-title" style={{ margin: 0 }}>Role Requirements</div>
                </div>
                {allRoles.map((role) => {
                  const m = roleReqs.find((rq) => rq.role === role);
                  const mand = m?.mandatory ?? [];
                  const benef = (m?.beneficial ?? []).filter((b) => !mandatoryForRoles.includes(b));
                  return (
                    <div key={role} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                        <div style={{ fontSize: 10, color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', flex: 1 }}>{role}</div>
                      </div>
                      {mand.map((r) => {
                        const has = w.certs.find((cv) => cv.type === r);
                        const ok = has && has.status !== 'expired';
                        return (
                          <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4, fontSize: 11, padding: '3px 4px', borderRadius: 4, background: 'var(--surface)' }}>
                            <span onClick={() => setTab('training')} style={{ color: ok ? 'var(--live)' : 'var(--danger)', fontSize: 13, cursor: 'pointer' }}>{ok ? '✓' : '✗'}</span>
                            <span onClick={() => setTab('training')} style={{ color: ok ? 'var(--text)' : 'var(--danger)', flex: 1, cursor: 'pointer' }}>{r}</span>
                            {has ? <span style={{ fontSize: 9, color: 'var(--muted)' }}>{fmtDate(has.expiry)}</span> : <span className="badge b-expired" style={{ fontSize: 8 }}>missing</span>}
                          </div>
                        );
                      })}
                      {benef.length ? (
                        <>
                          <div style={{ fontSize: 9, color: 'var(--info)', letterSpacing: 1, textTransform: 'uppercase', margin: '5px 0 3px', paddingTop: 4, borderTop: '1px solid rgba(30,42,64,.3)' }}>Beneficial</div>
                          {benef.map((r) => {
                            const has = w.certs.find((cv) => cv.type === r);
                            return (
                              <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3, fontSize: 11, padding: '2px 4px', borderRadius: 4 }}>
                                <span style={{ color: has ? 'var(--live)' : 'var(--faint)' }}>{has ? '✓' : '○'}</span>
                                <span style={{ color: has ? 'var(--text)' : 'var(--muted)', flex: 1 }}>{r}</span>
                              </div>
                            );
                          })}
                        </>
                      ) : null}
                    </div>
                  );
                })}
                {beneficialForRoles.length === 0 && allRoles.every((r) => !roleReqs.find((rq) => rq.role === r)) ? (
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>No role requirements configured</div>
                ) : null}
              </div>
            </div>
          ) : null}

          {tab === 'training' ? (() => {
            const hasAlerts = certExp > 0 || certWarn > 0;
            const visibleCerts = w.certs.filter((c) => certFilter === 'all' || c.status === certFilter);
            const tileBase = (active: boolean): React.CSSProperties => ({
              borderRadius: 8,
              padding: '8px 14px',
              textAlign: 'center',
              flex: 1,
              minWidth: 70,
              cursor: 'pointer',
              transition: 'all .15s',
              ...(active ? { boxShadow: '0 0 0 2px var(--accent)', transform: 'translateY(-1px)' } : {}),
            });
            const toggleCert = (f: CertFilter) => setCertFilter((cur) => (cur === f ? 'all' : f));
            const filterLabel = certFilter === 'all' ? '' : certFilter === 'valid' ? 'Valid' : certFilter === 'expiring' ? 'Expiring' : 'Expired';
            return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setTab('profile')} style={{ fontSize: 11 }}>‹ Back to Profile</button>
                <div style={{ flex: 1 }}></div>
                {isAdmin ? <button className="btn btn-primary btn-sm" onClick={() => setAddCertInline(true)}>+ Add Certificate</button> : null}
                {hasAlerts && canUseComms ? (
                  <button className="btn btn-warn btn-sm" onClick={() => setNotifyExpiryOpen(true)}>🔔 Notify Expiry</button>
                ) : null}
                {missingMandatory.length > 0 && canUseComms ? (
                  <button className="btn btn-warn btn-sm" onClick={() => setNotifyMissingOpen(true)}>🔔 Notify Missing</button>
                ) : null}
                {canUseComms ? <button className="btn btn-ghost btn-sm" onClick={() => setShareOpen(true)} style={{ color: 'var(--info)', borderColor: 'var(--info)' }}>📤 Share Training</button> : null}
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <div role="button" tabIndex={0} onClick={() => toggleCert('all')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCert('all'); } }} style={{ ...tileBase(certFilter === 'all'), background: 'var(--card)', border: '1px solid var(--border)' }}>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 800 }}>{w.certs.length}</div>
                  <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase' }}>All</div>
                </div>
                <div role="button" tabIndex={0} onClick={() => toggleCert('valid')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCert('valid'); } }} style={{ ...tileBase(certFilter === 'valid'), background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.25)' }}>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 800, color: '#22C55E' }}>{certOk}</div>
                  <div style={{ fontSize: 9, color: '#22C55E', letterSpacing: 1, textTransform: 'uppercase' }}>Valid</div>
                </div>
                <div role="button" tabIndex={0} onClick={() => toggleCert('expiring')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCert('expiring'); } }} style={{ ...tileBase(certFilter === 'expiring'), background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.25)' }}>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 800, color: '#F59E0B' }}>{certWarn}</div>
                  <div style={{ fontSize: 9, color: '#F59E0B', letterSpacing: 1, textTransform: 'uppercase' }}>Expiring</div>
                </div>
                <div role="button" tabIndex={0} onClick={() => toggleCert('expired')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCert('expired'); } }} style={{ ...tileBase(certFilter === 'expired'), background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.25)' }}>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 800, color: '#EF4444' }}>{certExp}</div>
                  <div style={{ fontSize: 9, color: '#EF4444', letterSpacing: 1, textTransform: 'uppercase' }}>Expired</div>
                </div>
              </div>
              {certFilter !== 'all' ? (
                <div style={{ marginBottom: 10, fontSize: 11, color: 'var(--muted)' }}>
                  Showing: <strong style={{ color: 'var(--text)' }}>{filterLabel}</strong>
                  <button onClick={() => setCertFilter('all')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 11, marginLeft: 6 }}>× Clear filter</button>
                </div>
              ) : null}

              {missingMandatory.length > 0 ? (
                <div style={{ background: 'rgba(239,68,68,.08)', border: '2px solid rgba(239,68,68,.35)', borderRadius: 12, padding: 12, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(239,68,68,.2)', border: '2px solid #EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#EF4444', flexShrink: 0 }}>✗</div>
                    <div>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 12, color: '#EF4444' }}>Missing Mandatory Certificates ({missingMandatory.length})</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>These are required for {w.name}'s role but not on record</div>
                    </div>
                  </div>
                  {missingMandatory.map((ct) => {
                    const rolesNeedingIt = allRoles.filter((r) => (roleReqs.find((rq) => rq.role === r)?.mandatory ?? []).includes(ct));
                    return (
                      <div key={ct} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: 8, background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 8, marginBottom: 5, cursor: 'pointer' }} onClick={() => { if (!isAdmin) return; setNewCertType(ct); setAddCertInline(true); }} title={isAdmin ? 'Click to add this certificate' : 'Missing certificate'}>
                        <div style={{ color: '#EF4444', fontSize: 16, fontWeight: 800, flexShrink: 0 }}>✗</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#EF4444' }}>{ct}</div>
                          <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 1 }}>Required for: {rolesNeedingIt.join(', ')}</div>
                        </div>
                        <span style={{ background: 'rgba(239,68,68,.18)', color: '#EF4444', borderRadius: 5, padding: '2px 7px', fontSize: 9, fontWeight: 700, letterSpacing: 0.5 }}>MISSING</span>
                        {isAdmin ? <button className="btn btn-sm" style={{ background: '#EF4444', color: '#fff', border: 'none' }} onClick={(e) => { e.stopPropagation(); setAddCertInline(true); }}>+ Add</button> : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {w.certs.length === 0 && missingMandatory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)', border: '2px dashed var(--border)', borderRadius: 10 }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>◫</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>No certificates recorded</div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>Click "+ Add Certificate" to add the first one</div>
                </div>
              ) : null}

              {visibleCerts.map((cert) => {
                const isMandatory = mandatoryForRoles.some((m) => certTypeMatches(cert.type, m));
                const isBeneficial = beneficialForRoles.some((b) => certTypeMatches(cert.type, b));
                const sCol = certStatusColor(cert.status);
                const sIcon = cert.status === 'valid' ? '✓' : cert.status === 'expiring' ? '⏱' : '✗';
                const isEditing = editCertInlineId === cert.id;
                return (
                  <div key={cert.id} style={{ background: 'var(--card)', border: `1px solid ${cert.status === 'expired' ? 'rgba(239,68,68,.3)' : cert.status === 'expiring' ? 'rgba(245,158,11,.25)' : 'var(--border)'}`, borderRadius: 12, padding: 13, marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: certStatusBg(cert.status), border: `2px solid ${sCol}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: sCol, flexShrink: 0 }}>{sIcon}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{cert.type}</div>
                        <div style={{ display: 'flex', gap: 5, marginTop: 3, flexWrap: 'wrap' }}>
                          {isMandatory ? <span style={{ background: 'rgba(232,164,39,.18)', color: 'var(--accent)', borderRadius: 4, padding: '1px 6px', fontSize: 9, fontWeight: 600, letterSpacing: 0.5 }}>MANDATORY</span> : null}
                          {isBeneficial ? <span style={{ background: 'rgba(59,130,246,.15)', color: 'var(--info)', borderRadius: 4, padding: '1px 6px', fontSize: 9, fontWeight: 600 }}>BENEFICIAL</span> : null}
                          <span style={{ color: sCol, fontSize: 10, fontWeight: 500 }}>Expires: {fmtDate(cert.expiry)}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                        {isAdmin ? <button className="btn btn-ghost btn-sm" onClick={() => setEditCertInlineId(isEditing ? null : cert.id)}>{isEditing ? '— Cancel' : '✏ Edit'}</button> : null}
                        {isAdmin ? <button className="btn btn-sm" style={{ background: 'rgba(239,68,68,.15)', color: '#EF4444', border: '1px solid rgba(239,68,68,.3)' }} onClick={() => { if (confirm(`Delete ${cert.type}?`)) removeCert.mutate(cert.id); }}>✕</button> : null}
                      </div>
                    </div>
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 7, fontFamily: 'Syne, sans-serif' }}>📎 Certificate Document</div>
                      {(cert.docUrl || cert.docRef) ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 9px', background: 'rgba(59,130,246,.15)', border: '1px solid rgba(59,130,246,.25)', borderRadius: 7 }}>
                          <span style={{ fontSize: 16 }}>📄</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {cert.docUrl ? (
                              <a href={cert.docUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 500, color: 'var(--info)', textDecoration: 'underline', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title="Open certificate document">{cert.docRef || cert.docUrl.split('/').pop()}</a>
                            ) : (
                              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--info)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title="Reference only — re-upload the file to view it">{cert.docRef}</div>
                            )}
                          </div>
                          {isAdmin ? <button className="btn btn-ghost btn-sm" onClick={() => removeCertDoc.mutate(cert.id)} style={{ color: 'var(--danger)' }} title="Remove document">✕</button> : null}
                        </div>
                      ) : !isAdmin ? null : (
                        <div
                          style={{ padding: 12, borderRadius: 8, border: '2px dashed var(--border)', textAlign: 'center', cursor: 'pointer', background: 'var(--card)' }}
                          onDragOver={(e) => { e.preventDefault(); (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--accent)'; }}
                          onDragLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; }}
                          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) uploadCertDoc.mutate({ certId: cert.id, file: f }); }}
                          onClick={() => document.getElementById(`cert-file-${cert.id}`)?.click()}
                        >
                          <div style={{ fontSize: 18, marginBottom: 4 }}>📂</div>
                          <div style={{ fontSize: 11, fontWeight: 500 }}>Drop certificate file here</div>
                          <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 2 }}>PDF, JPG, PNG — or click to browse</div>
                          <input type="file" id={`cert-file-${cert.id}`} accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCertDoc.mutate({ certId: cert.id, file: f }); }} />
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <CertEditInline
                        cert={cert}
                        onCancel={() => setEditCertInlineId(null)}
                        onSave={(payload) => updateCert.mutate({ certId: cert.id, ...payload })}
                        saving={updateCert.isPending}
                      />
                    ) : null}
                  </div>
                );
              })}
              {addCertInline ? (
                <div style={{ background: 'var(--card)', border: '2px dashed var(--accent)', borderRadius: 12, padding: 14, marginTop: 4 }}>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 11 }}>+ New Certificate</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginBottom: 9 }}>
                    <div>
                      <label className="form-label">Certificate Type</label>
                      <input type="text" className="form-input" placeholder="e.g. SMSTS, CSCS, First Aid" value={newCertType} onChange={(e) => setNewCertType(e.target.value)} />
                    </div>
                    <div>
                      <label className="form-label">Expiry Date</label>
                      <input type="date" className="form-input" value={newCertExpiry} onChange={(e) => setNewCertExpiry(e.target.value)} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginBottom: 9 }}>
                    <div>
                      <label className="form-label">Status</label>
                      <select className="form-select" value={newCertStatus} onChange={(e) => setNewCertStatus(e.target.value as 'valid' | 'expiring' | 'expired')}>
                        <option value="valid">✓ Valid</option>
                        <option value="expiring">⏱ Expiring Soon</option>
                        <option value="expired">✗ Expired</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Document Reference (optional)</label>
                      <input type="text" className="form-input" placeholder="e.g. SMSTS-2024-001.pdf" value={newCertDocRef} onChange={(e) => setNewCertDocRef(e.target.value)} />
                    </div>
                  </div>
                  <div
                    onClick={() => document.getElementById('new-cert-file-input')?.click()}
                    onDragOver={(e) => { e.preventDefault(); if (!newCertDropOver) setNewCertDropOver(true); }}
                    onDragLeave={() => setNewCertDropOver(false)}
                    onDrop={(e) => { e.preventDefault(); setNewCertDropOver(false); const f = e.dataTransfer.files?.[0]; if (f) setNewCertFile(f); }}
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      border: `2px dashed ${newCertDropOver ? 'var(--accent)' : 'var(--border)'}`,
                      textAlign: 'center',
                      cursor: 'pointer',
                      background: newCertDropOver ? 'rgba(232,164,39,.08)' : 'var(--surface)',
                      marginBottom: 9,
                      transition: 'border-color .12s, background .12s',
                    }}
                  >
                    <div style={{ fontSize: 18, marginBottom: 4 }}>📂</div>
                    {newCertFile ? (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--info)' }}>📄 {newCertFile.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 2 }}>
                          Click to choose a different file ·
                          <button type="button" onClick={(e) => { e.stopPropagation(); setNewCertFile(null); }} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 10, marginLeft: 4, padding: 0 }}>remove</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 500 }}>Drop certificate document here or click to attach</div>
                        <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 2 }}>PDF, JPG, PNG</div>
                      </>
                    )}
                    <input
                      type="file"
                      id="new-cert-file-input"
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) setNewCertFile(f); e.target.value = ''; }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 9 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setAddCertInline(false); setNewCertType(''); setNewCertExpiry(''); setNewCertStatus('valid'); setNewCertDocRef(''); setNewCertFile(null); }}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={() => saveNewCert.mutate()} disabled={saveNewCert.isPending || !newCertType.trim()}>
                      {saveNewCert.isPending ? 'Saving…' : '✓ Save Certificate'}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          );})() : null}

          {tab === 'deploy' ? (
            (() => {
              const active = deployments.filter((d) => d.status === 'active').length;
              const completed = deployments.filter((d) => d.status === 'completed').length;
              const filteredDeploys = deployments.filter((d) => {
                if (deployFilter === 'active') return d.status === 'active';
                if (deployFilter === 'completed') return d.status === 'completed';
                return true;
              });
              const toggleDeploy = (f: DeployFilter) => setDeployFilter((cur) => (cur === f ? 'all' : f));
              const cardBase = { borderRadius: 8, padding: '8px 14px', textAlign: 'center' as const, minWidth: 100, cursor: 'pointer' as const, transition: 'transform .08s ease, box-shadow .08s ease' };
              const activeRing = (color: string) => ({ boxShadow: `0 0 0 2px ${color}`, transform: 'translateY(-1px)' });
              const filterLbl = deployFilter === 'all' ? '' : deployFilter === 'active' ? ' (Active only)' : ' (Completed only)';
              return (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setTab('profile')} style={{ fontSize: 11 }}>‹ Back to Profile</button>
                    <div style={{ flex: 1 }}></div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {deployFilter === 'all' ? `${deployments.length} total` : `${filteredDeploys.length} of ${deployments.length}${filterLbl}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleDeploy('active')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDeploy('active'); } }}
                      title={deployFilter === 'active' ? 'Clear filter' : 'Filter to Active only'}
                      style={{ ...cardBase, background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.25)', ...(deployFilter === 'active' ? activeRing('#22C55E') : {}) }}
                    >
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 800, color: '#22C55E' }}>{active}</div>
                      <div style={{ fontSize: 9, color: '#22C55E', letterSpacing: 1, textTransform: 'uppercase' }}>Active</div>
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleDeploy('completed')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDeploy('completed'); } }}
                      title={deployFilter === 'completed' ? 'Clear filter' : 'Filter to Completed only'}
                      style={{ ...cardBase, background: 'rgba(107,122,153,.1)', border: '1px solid rgba(107,122,153,.25)', ...(deployFilter === 'completed' ? activeRing('var(--muted)') : {}) }}
                    >
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 800, color: 'var(--muted)' }}>{completed}</div>
                      <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase' }}>Completed</div>
                    </div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <a className="btn btn-ghost btn-sm" href="/deploylog" onClick={(e) => { e.preventDefault(); navigate('/deploylog'); onClose(); }}>View All in Deploy Log →</a>
                    </div>
                  </div>
                  <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: absences.length ? 8 : 0 }}>
                      <div className="sec-title" style={{ margin: 0 }}>Sick / Holiday Periods ({absences.length})</div>
                      {isAdmin ? <button className="btn btn-ghost btn-sm" onClick={() => setAbsenceOpen(true)}>+ Record Absence</button> : null}
                    </div>
                    {absences.map((a) => (
                      <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid rgba(30,42,64,.4)', fontSize: 11 }}>
                        <span style={{ background: a.type === 'sick' ? 'rgba(245,158,11,.18)' : 'rgba(96,165,250,.18)', color: a.type === 'sick' ? '#f59e0b' : '#60a5fa', borderRadius: 999, padding: '1px 8px', fontSize: 9, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>
                          {a.type === 'sick' ? '🤒' : '🏖'} {ABSENCE_TYPE_LABELS[a.type]}
                        </span>
                        <span style={{ fontWeight: 500 }}>{a.startDate.slice(0, 10)} → {a.endDate.slice(0, 10)}</span>
                        {a.notes ? <span style={{ color: 'var(--muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.notes}</span> : <span style={{ flex: 1 }}></span>}
                        {a.createdBy ? <span style={{ color: 'var(--faint)', fontSize: 9 }}>by {a.createdBy}</span> : null}
                        {isAdmin ? <button onClick={() => { if (confirm('Remove this absence?')) deleteAbsence.mutate(a.id); }} style={{ background: 'none', border: 'none', color: 'var(--faint)', cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: '0 2px' }} title="Remove">✕</button> : null}
                      </div>
                    ))}
                  </div>
                  {deployments.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)', fontSize: 12 }}>No deployment records found for this worker</div>
                  ) : filteredDeploys.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)', fontSize: 12 }}>No deployments match this filter</div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ margin: 0 }}>
                        <thead><tr><th>Ref</th><th>Project</th><th>Start</th><th>Duration</th><th>Status</th><th>WA</th></tr></thead>
                        <tbody>
                          {filteredDeploys.map((d) => {
                            const start = d.startDate ? new Date(d.startDate) : null;
                            const end = d.endDate ? new Date(d.endDate) : null;
                            const days = start && end ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000)) : null;
                            const durationLabel = d.duration && d.duration.trim()
                              ? d.duration
                              : (days != null ? `${days} day${days !== 1 ? 's' : ''}` : '—');
                            const projectLabel = d.projectName ?? '—';
                            return (
                              <tr key={d.id} onClick={() => setOpenDeployId(d.id)} style={{ cursor: 'pointer' }} title="Open deployment record">
                                <td style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 9, color: 'var(--faint)' }}>{d.id.slice(0, 10)}</td>
                                <td><span style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11 }}>{projectLabel}</span></td>
                                <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(d.startDate)}</td>
                                <td style={{ fontSize: 11 }}>{durationLabel}</td>
                                <td><span className={`badge b-${d.status}`} style={{ fontSize: 9 }}>{d.status}</span></td>
                                <td style={{ textAlign: 'center', color: 'var(--faint)' }}>—</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()
          ) : null}

          {tab === 'signin' ? (
            (() => {
              const ins = signinRecords.filter((r) => r.type === 'in').length;
              const outs = signinRecords.filter((r) => r.type === 'out').length;
              const today = new Date().toISOString().slice(0, 10);
              const todayCount = signinRecords.filter((r) => r.timestamp.slice(0, 10) === today).length;
              const filtered = signinRecords.filter((r) => {
                if (signinFilter === 'in') return r.type === 'in';
                if (signinFilter === 'out') return r.type === 'out';
                if (signinFilter === 'today') return r.timestamp.slice(0, 10) === today;
                return true;
              });
              const toggle = (f: SigninFilter) => setSigninFilter((cur) => (cur === f ? 'all' : f));
              const cardBase = { flex: 1, padding: 14, borderRadius: 10, textAlign: 'center' as const, minWidth: 120, cursor: 'pointer' as const, transition: 'transform .08s ease, box-shadow .08s ease' };
              const activeRing = (color: string) => ({ boxShadow: `0 0 0 2px ${color}`, transform: 'translateY(-1px)' });
              const filterLabel = signinFilter === 'all' ? '' : signinFilter === 'in' ? ' (Sign Ins only)' : signinFilter === 'out' ? ' (Sign Outs only)' : ' (Today only)';
              return (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setTab('profile')} style={{ fontSize: 11 }}>‹ Back</button>
                    <div style={{ flex: 1 }}></div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {signinFilter === 'all' ? `${signinRecords.length} total records` : `${filtered.length} of ${signinRecords.length} records${filterLabel}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggle('in')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle('in'); } }}
                      title={signinFilter === 'in' ? 'Clear filter' : 'Filter to Sign Ins only'}
                      style={{ ...cardBase, background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.25)', ...(signinFilter === 'in' ? activeRing('#22C55E') : {}) }}
                    >
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 800, color: '#22C55E' }}>{ins}</div>
                      <div style={{ fontSize: 10, color: '#22C55E', letterSpacing: 1, textTransform: 'uppercase' }}>Sign Ins</div>
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggle('out')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle('out'); } }}
                      title={signinFilter === 'out' ? 'Clear filter' : 'Filter to Sign Outs only'}
                      style={{ ...cardBase, background: 'var(--card)', border: '1px solid var(--border)', ...(signinFilter === 'out' ? activeRing('var(--muted)') : {}) }}
                    >
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--muted)' }}>{outs}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase' }}>Sign Outs</div>
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggle('today')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle('today'); } }}
                      title={signinFilter === 'today' ? 'Clear filter' : 'Filter to today only'}
                      style={{ ...cardBase, background: 'rgba(59,130,246,.1)', border: '1px solid rgba(59,130,246,.25)', ...(signinFilter === 'today' ? activeRing('var(--info)') : {}) }}
                    >
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--info)' }}>{todayCount}</div>
                      <div style={{ fontSize: 10, color: 'var(--info)', letterSpacing: 1, textTransform: 'uppercase' }}>Today</div>
                    </div>
                  </div>
                  {signinRecords.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)', fontSize: 12 }}>No sign in/out records for this worker yet</div>
                  ) : filtered.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)', fontSize: 12 }}>No records match this filter</div>
                  ) : (
                    <table>
                      <thead><tr><th>Type</th><th>Project</th><th>Time</th></tr></thead>
                      <tbody>
                        {filtered.map((r) => {
                          const projectLabel = r.projectName ?? '—';
                          return (
                            <tr key={r.id}>
                              <td>{r.type === 'in' ? <span style={{ color: '#22C55E' }}>✅ In</span> : <span style={{ color: 'var(--muted)' }}>🚪 Out</span>}</td>
                              <td>
                                {canOpenProject ? (
                                  <a
                                    href={`/projects?open=${encodeURIComponent(r.projectId)}`}
                                    onClick={(e) => { e.preventDefault(); navigate(`/projects?open=${encodeURIComponent(r.projectId)}`); onClose(); }}
                                    style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, cursor: 'pointer', textDecoration: 'none' }}
                                  >
                                    {projectLabel}
                                  </a>
                                ) : (
                                  <span style={{ fontSize: 11 }}>{projectLabel}</span>
                                )}
                              </td>
                              <td style={{ fontSize: 11 }}>{new Date(r.timestamp).toLocaleString('en-GB')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()
          ) : null}

          {tab === 'signeddocs' ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setTab('profile')} style={{ fontSize: 11 }}>‹ Back</button>
                <div style={{ flex: 1 }}></div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{signedDocsCount} of {workerDocs.length} signed</div>
              </div>
              {workerDocs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 50, color: 'var(--muted)' }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>✍</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>No documents shared yet</div>
                  <div style={{ fontSize: 11, marginTop: 4, color: 'var(--faint)' }}>Documents shared with this worker will appear here.</div>
                </div>
              ) : (
                <table style={{ margin: 0, fontSize: 11 }}>
                  <thead><tr><th>Document</th><th>Shared</th><th>Notified</th><th>Status</th><th>Signed</th></tr></thead>
                  <tbody>
                    {workerDocs.map((d) => (
                      <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/docsign')}>
                        <td style={{ fontWeight: 600 }}>{d.name}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(d.createdAt).toLocaleDateString('en-GB')}{d.sharedBy ? <div style={{ fontSize: 9, color: 'var(--muted)' }}>by {d.sharedBy}</div> : null}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{d.notifiedAt ? `✉ ${new Date(d.notifiedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : '—'}</td>
                        <td>{d.status === 'signed' ? <span className="sign-chip-signed">✓ SIGNED</span> : d.status === 'declined' ? <span className="sign-chip-pending">✕ DECLINED</span> : <span className="sign-chip-pending">⏱ PENDING</span>}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{d.signedAt ? new Date(d.signedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}

          {tab === 'dna' ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setTab('profile')} style={{ fontSize: 11 }}>‹ Back to Profile</button>
                <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 700, marginLeft: 4 }}>⚠ {dnaForWorker.length} DNA record{dnaForWorker.length !== 1 ? 's' : ''} on file</div>
                <div style={{ flex: 1 }}></div>
                {isAdmin ? <button className="btn btn-ghost btn-sm" onClick={() => setDnaAddOpen(true)} style={{ fontSize: 11 }}>+ Add DNA</button> : null}
              </div>
              {dnaForWorker.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: 'var(--live)' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>✓</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>No DNA records for this worker</div>
                </div>
              ) : (
                <div>
                  {dnaForWorker.map((d) => {
                    const proj = allProjects.find((p) => p.id === d.projectId);
                    const projCode = proj?.code ?? '';
                    const projName = d.projectName ?? proj?.name ?? '—';
                    const datePart = d.scheduledDate ? d.scheduledDate.slice(0, 10) : '—';
                    const timePart = d.scheduledTime ?? '';
                    const recordedDate = d.recordedAt ?? d.createdAt;
                    const recordedShort = recordedDate ? new Date(recordedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                    return (
                      <div key={d.id} style={{ background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 10, padding: '11px 14px', marginBottom: 8, position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 3 }}>
                              {datePart}{timePart ? <span style={{ marginLeft: 8, color: 'var(--muted)', fontWeight: 500 }}>{timePart}</span> : null}
                            </div>
                            <div style={{ fontSize: 11, marginBottom: 6 }}>
                              {projCode ? <span style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{projCode}</span> : null}
                              {projCode ? <span style={{ color: 'var(--muted)', margin: '0 6px' }}>—</span> : null}
                              <span style={{ color: 'var(--text)' }}>{projName}</span>
                            </div>
                            {d.notes ? <div style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--text)', marginBottom: 6, lineHeight: 1.5 }}>{d.notes}</div> : null}
                            <div style={{ fontSize: 10, color: 'var(--faint)' }}>
                              {d.recordedBy ? <>by <span style={{ color: 'var(--muted)' }}>{d.recordedBy}</span></> : null}
                              {d.recordedBy && recordedDate ? <span> · </span> : null}
                              {recordedDate ? recordedShort : null}
                              {d.code ? <span> · <span style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{d.code}</span></span> : null}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="btn btn-ghost btn-xs"
                              onClick={() => { if (isAdmin) setDnaEdit(d); }}
                              title="Edit"
                              style={{ fontSize: 12, padding: '2px 8px' }}
                            >—</button>
                            {isAdmin ? <button
                              onClick={() => { if (confirm(`Delete DNA record ${d.code}? This cannot be undone.`)) deleteDna.mutate(d.id); }}
                              title="Delete"
                              style={{ background: 'rgba(239,68,68,.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,.35)', borderRadius: 6, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}
                            >×</button> : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          {tab === 'notes' ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setTab('profile')} style={{ fontSize: 11 }}>‹ Back to Profile</button>
              </div>
              {isAdmin ? <div style={{ marginBottom: 14, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 13 }}>
                <div className="sec-title" style={{ marginBottom: 8 }}>Add New Note</div>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Type note here…"
                  style={{ minHeight: 80, width: '100%', marginBottom: 8, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 6, padding: 8, fontFamily: 'DM Sans, sans-serif', fontSize: 12, resize: 'vertical' }}
                />
                <button className="btn btn-primary btn-sm" onClick={() => { if (noteText.trim()) addNote.mutate(noteText.trim()); }} disabled={addNote.isPending || !noteText.trim()}>+ Add Note</button>
              </div> : null}
              <div>
                {w.notes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)', fontSize: 12 }}>No notes yet for this worker</div>
                ) : (
                  w.notes.map((n) => (
                    <div key={n.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '11px 13px', marginBottom: 8, position: 'relative' }}>
                      <div style={{ fontSize: 12, lineHeight: 1.6, marginBottom: 6, paddingRight: 22 }}>{n.text}</div>
                      <div style={{ fontSize: 9, color: 'var(--faint)' }}>Added by {n.by ?? 'unknown'} on {fmtDate(n.date)}</div>
                      {isAdmin ? <button onClick={() => { if (confirm('Delete this note?')) deleteNote.mutate(n.id); }} style={{ position: 'absolute', top: 9, right: 9, background: 'none', border: 'none', color: 'var(--faint)', cursor: 'pointer', fontSize: 16 }} title="Delete">×</button> : null}
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {editOpen ? <AddWorkerModal onClose={() => setEditOpen(false)} initial={w as any} /> : null}
      {notifyExpiryOpen ? <CertNotifyModal onClose={() => setNotifyExpiryOpen(false)} workerId={workerId} /> : null}
      {notifyMissingOpen ? <CertNotifyModal onClose={() => setNotifyMissingOpen(false)} workerId={workerId} mode="missing" /> : null}
      {absenceOpen && w ? <AbsenceModal onClose={() => setAbsenceOpen(false)} workerId={workerId} workerName={w.name} /> : null}
      {proxOpen ? <ProximityModal onClose={() => setProxOpen(false)} workerId={workerId} workerName={w.name} workerPhone={w.phone} workerPostcode={w.postcode} /> : null}
      {notesOpen ? <WorkerNotesModal onClose={() => setNotesOpen(false)} workerId={workerId} /> : null}
      {shareOpen ? <ShareTrainingModal onClose={() => setShareOpen(false)} workerId={workerId} /> : null}
      {photoOpen ? <PhotoModal onClose={() => setPhotoOpen(false)} workerId={workerId} workerName={w.name} /> : null}
      {qrOpen ? <QrSignInModal onClose={() => setQrOpen(false)} workerId={workerId} workerName={w.name} /> : null}
      {dnaAddOpen ? <DnaEditModal onClose={() => setDnaAddOpen(false)} workerId={workerId} workerName={w.name} /> : null}
      {openDeployId ? <DeploymentDetailModal onClose={() => setOpenDeployId(null)} deploymentId={openDeployId} /> : null}
      {dnaEdit ? (
        <DnaEditModal
          onClose={() => setDnaEdit(null)}
          dnaId={dnaEdit.id}
          dnaCode={dnaEdit.code}
          workerId={workerId}
          workerName={w.name}
          initial={{
            projectId: dnaEdit.projectId,
            scheduledDate: dnaEdit.scheduledDate,
            scheduledTime: dnaEdit.scheduledTime,
            recordedBy: dnaEdit.recordedBy ?? '',
            notes: dnaEdit.notes ?? '',
          }}
        />
      ) : null}
    </div>,
    document.body,
  );
}
